import { Checkbox, Spin } from 'antd';
import _ from 'lodash';
import { forwardRef, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import InfiniteScroller from 'components/molecules/InfiniteScroller';
import { UserContext } from 'contexts/ConfigContext';
import { getConfig } from 'utils/helperMethods';
import { ConfigKeys } from 'utils/Enums';
import SearchBox from 'components/molecules/SearchBox';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import { useCommonApiHandler } from 'hooks/general/useCommonApiHandler';

type AttributesPanelProps = {
    searchKey: string;
    setSearchKey: Function;
    offset: number;
    setOffset: Function;
    selectedFilter: any;
    tempApplyFilterObject: any;
    setTempApplyFilterObject: Function;
    tempAllCheckedAttribute: any;
    setTempAllCheckedAttribute: Function;
    tempAllSelectedValueForAllAttribute: any;
    setTempAllSelectedValueForAllAttribute: Function;
    showAttributePanelToLeft?: boolean;
    url: string;
    variables: any;
};
const AttributesPanel = forwardRef<HTMLDivElement, AttributesPanelProps>(
    (props: AttributesPanelProps, attributePanelRef) => {
        const {
            // scenarioId,
            selectedFilter,
            tempApplyFilterObject,
            setTempApplyFilterObject,
            tempAllCheckedAttribute,
            setTempAllCheckedAttribute,
            tempAllSelectedValueForAllAttribute,
            setTempAllSelectedValueForAllAttribute,
            offset,
            setOffset,
            searchKey,
            setSearchKey,
            showAttributePanelToLeft = false,
            url,
            variables,
        } = props;
        const { appConfig } = useContext(UserContext);

        // const { dimensionConfigData } = useContext(pricingSimulatorWrapperDataContext);

        const [localSearchKey, setLocalSearchKey] = useState('');
        const { scenarioFilterOffsetLimit } = getConfig(ConfigKeys.PRICING_SIMULATOR, appConfig);
        const [attributesData, setData] = useState<any | null>(null);
        const [count, setCount] = useState<any | null>(null);

        const { loading: attributeLoading } = useCommonApiHandler({
            queryKey: ['group-attributes', { ...variables, offset, searchKey }],
            method: 'post',
            variables: { ...variables, offset, search_key: searchKey },
            queryConfig: {
                enabled: !!selectedFilter?.value,
            },
            url: url,
            parserFunction: (res) => {
                let parsedData = [];
                if (offset) {
                    parsedData = res?.attribute_names?.map((name: string) => {
                        return {
                            label: name,
                            value: name,
                        };
                    });
                    // updating attribute property if select all has been checked
                    if (tempAllSelectedValueForAllAttribute[selectedFilter?.value]) {
                        const newTempAllCheckedAttribute = _.cloneDeep(tempAllCheckedAttribute);
                        parsedData.forEach((item: any) =>
                            newTempAllCheckedAttribute[selectedFilter.value].add(item.value),
                        );
                        setTempAllCheckedAttribute(newTempAllCheckedAttribute);
                    }
                    setData([...attributesData, ...parsedData]);

                    return { data: [...attributesData, ...parsedData], count: 0 };
                } else {
                    const parsedData: any = res?.attribute_names?.map((name: string) => {
                        return {
                            label: name,
                            value: name,
                        };
                    });
                    setCount(res.total_count || 0);
                    setData(parsedData);
                    return { data: parsedData, count: res.total_count || 0 };
                }
            },
        });

        const handleIndividualCheckBoxClick = (checked: boolean, attributeValue: number) => {
            if (checked) {
                const newAllCheckedAttribute = _.cloneDeep(tempAllCheckedAttribute);
                newAllCheckedAttribute[selectedFilter.value].add(attributeValue);
                setTempAllCheckedAttribute(newAllCheckedAttribute);
                handleInclusionAndExclusion(true, attributeValue);
            } else {
                const newAllCheckedAttribute = _.cloneDeep(tempAllCheckedAttribute);
                newAllCheckedAttribute[selectedFilter.value].delete(attributeValue);
                setTempAllCheckedAttribute(newAllCheckedAttribute);
                handleInclusionAndExclusion(false, attributeValue);
            }
        };

        // TODO : enable for v2 version
        const handleSelectAllClick = (checked: boolean) => {
            if (checked) {
                // updated selectAll property for selected Attribute
                setTempAllSelectedValueForAllAttribute({
                    ...tempAllSelectedValueForAllAttribute,
                    [selectedFilter.value]: checked,
                });
                // update selected property for all the individual attribute to true
                const newAllCheckedAttribute = _.cloneDeep(tempAllCheckedAttribute);
                newAllCheckedAttribute[selectedFilter.value] = new Set(attributesData.map((a: any) => a.value));
                setTempAllCheckedAttribute(newAllCheckedAttribute);
            } else {
                updateSelectAllPropertyByValue(false);
                setTempAllCheckedAttribute({
                    ...tempAllCheckedAttribute,
                    [selectedFilter.value]: new Set(),
                });
            }
            // Reset inclusion and Exclusion for Selected Filter
            resetInclusionAndExclusion();
        };

        const resetInclusionAndExclusion = () => {
            const updatedApplyFilterObj = _.cloneDeep(tempApplyFilterObject);
            updatedApplyFilterObj[selectedFilter.value] = {};
            setTempApplyFilterObject(updatedApplyFilterObj);
        };

        const updateSelectAllPropertyByValue = (value: boolean) => {
            setTempAllSelectedValueForAllAttribute({
                ...tempAllSelectedValueForAllAttribute,
                [selectedFilter.value]: value,
            });
        };

        // inclusion has some items
        const handleInclusionAndExclusion = (value: boolean, attributeValue: any) => {
            const selectedFilterValue = selectedFilter.value;
            const updatedApplyFilterObj = _.cloneDeep(tempApplyFilterObject);
            const objKey = tempAllSelectedValueForAllAttribute[selectedFilterValue] ? 'exclusion' : 'inclusion';
            const inclusionOrExclusionObj = updatedApplyFilterObj?.[selectedFilterValue]?.[objKey] || new Set();

            // select all is set
            if (objKey === 'inclusion') {
                if (value) {
                    inclusionOrExclusionObj.add(attributeValue);
                } else {
                    inclusionOrExclusionObj.delete(attributeValue);
                }
            } else {
                if (value) {
                    inclusionOrExclusionObj.delete(attributeValue);
                } else {
                    inclusionOrExclusionObj.add(attributeValue);
                }
            }
            updatedApplyFilterObj[selectedFilterValue][objKey] = inclusionOrExclusionObj;

            setTempApplyFilterObject(updatedApplyFilterObj);
        };

        const infiniteScrollerOnNext = useCallback(() => {
            if (!attributeLoading) {
                const newOffset = offset + scenarioFilterOffsetLimit;
                setOffset(newOffset);
            }
        }, [attributeLoading]);
        //syncing context search key with component
        useEffect(() => {
            if (searchKey !== localSearchKey) setLocalSearchKey(searchKey || '');
        }, [searchKey]);

        //called when clicked on search icon or enter
        const onSearch = (val: string) => {
            setSearchKey(val);
            setOffset(0);
        };

        return (
            <div
                className={`absolute top-9 p-3 bg-white z-50 shadow-[0px_9px_28px_8px_rgba(0,0,0,0.05)] rounded-lg flex flex-col   w-[264px] ${
                    showAttributePanelToLeft ? 'right-[261px]' : ' left-[83px] '
                }  ${attributeLoading ? 'opacity-80' : ''}`}
                ref={attributePanelRef}
            >
                <div className="ml-[10px]">
                    <SearchBox
                        placeholder={`Search ${selectedFilter.label}...`}
                        disabled={attributeLoading}
                        onSearch={onSearch}
                        search={localSearchKey}
                        setSearch={setLocalSearchKey}
                    />
                </div>

                <div className={`w-full relative  ${attributeLoading ? ' pointer-events-none' : ''}`}>
                    {attributeLoading && !offset && (
                        <div className="flex absolute justify-center items-center w-full h-96">
                            <Spin />
                        </div>
                    )}
                    {attributesData?.length === 0 ? (
                        <div className="flex justify-center items-center w-full h-96 ">
                            <ErrorBox msg={'NoMatchingRecords'} />
                        </div>
                    ) : (
                        <InfiniteScroller
                            data={attributesData}
                            loading={attributeLoading}
                            hasMore={attributesData?.length < count}
                            height={384}
                            onNext={infiniteScrollerOnNext}
                        >
                            <div>
                                {/* select all should not be there when search key is present, since its paginated we can't enable */}
                                {!attributeLoading && !searchKey && (
                                    <Checkbox
                                        checked={tempAllSelectedValueForAllAttribute[selectedFilter?.value]}
                                        onChange={(e: any) => handleSelectAllClick(e.target.checked)}
                                        className="!ml-0 !py-2 !px-3 w-full"
                                    >
                                        <span className="font-xs">Select All</span>
                                    </Checkbox>
                                )}
                                {attributesData?.map((attribute: any, index: number) => {
                                    return (
                                        <Checkbox
                                            checked={tempAllCheckedAttribute[selectedFilter?.value]?.has(
                                                attribute.value,
                                            )}
                                            onChange={(e) =>
                                                handleIndividualCheckBoxClick(e.target.checked, attribute.value)
                                            }
                                            className="!ml-0 !py-2 !px-3 w-full attrInnerChild "
                                        >
                                            <span className="block w-full break-words whitespace-normal font-xs">
                                                {attribute.label}
                                            </span>
                                        </Checkbox>
                                    );
                                })}
                            </div>
                        </InfiniteScroller>
                    )}
                </div>
            </div>
        );
    },
);

export default AttributesPanel;
